import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Compass,
  Home,
  KeyRound,
  Layers,
  Package,
  ParkingCircle,
  Ruler,
  Sun,
  Thermometer,
  Trees,
  Zap,
  PlayCircle,
  Maximize2,
  FileText,
  View,
  X,
  Receipt,
} from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl, formatArea, formatPriceHuf } from "@/lib/format";
import { arePricesHidden, computeUnitPrice } from "@shared/pricing";
import type { UnitPrice, PriceLine } from "@shared/pricing";
import { useDocumentTitle } from "@/lib/use-document-title";
import { ScrollReveal } from "@/components/scroll-reveal";
import { ImageGallery, Lightbox } from "@/components/image-gallery";
import { DynamicForm } from "@/components/dynamic-form";
import { MortgageCalculator } from "@/components/mortgage-calculator";
import { MortgageAdvisorBox } from "@/components/mortgage-advisor";
import { MORTGAGE_CALCULATOR_ENABLED } from "@/lib/feature-flags";
import { Neighborhood } from "@/components/neighborhood";
import { QUOTE_ANCHOR, REQUEST_QUOTE_LABEL } from "@/components/request-quote";
import { useSiteSettings } from "@/lib/cms";
import {
  UNIT_STATUS_BADGE,
  UNIT_STATUS_LABEL,
  unitSlug,
} from "@/lib/unit-helpers";
import type {
  Floorplan,
  Project,
  ProjectUnit,
  StylePreview,
} from "@shared/schema";

type Detail = { project: Project; units: ProjectUnit[] };

export function UnitDetailPage({
  slug,
  unitSlug: unitSlugParam,
}: {
  slug: string;
  unitSlug: string;
}) {
  const [, navigate] = useLocation();
  const siteSettings = useSiteSettings();
  const q = useQuery({
    queryKey: ["project", slug],
    queryFn: () => api<Detail>(`/api/projects/${slug}`, { withAuth: false }),
  });

  const unit = useMemo(() => {
    if (!q.data) return null;
    return q.data.units.find((u) => unitSlug(u) === unitSlugParam) ?? null;
  }, [q.data, unitSlugParam]);

  useDocumentTitle(
    unit ? `${unit.label} · ${q.data?.project.name ?? ""}` : "Lakás",
  );

  // Ajánlatkérés-horgony: a lakáslistákból a „Kérjen ajánlatot" hivatkozás
  // `#ajanlatkeres`-sel érkezik. SPA-navigációnál a böngésző nem görget a
  // horgonyra (ráadásul a ScrollToTop előbb a lap tetejére ugrik), ezért a
  // lakás betöltése után magunk görgetünk az érdeklődő-űrlaphoz.
  useEffect(() => {
    if (!unit) return;
    if (window.location.hash !== `#${QUOTE_ANCHOR}`) return;
    const t = setTimeout(() => {
      document
        .getElementById(QUOTE_ANCHOR)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [unit?.id]);

  if (q.isLoading) {
    return (
      <div className="container py-24 text-brand-500">Betöltés...</div>
    );
  }

  if (q.isError || !q.data || !unit) {
    return (
      <div className="container py-24 text-center">
        <h1 className="section-title text-3xl">A lakás nem található</h1>
        <Link href={`/projektek/${slug}`} className="btn-primary mt-6 inline-flex">
          ← Vissza a projekthez
        </Link>
      </div>
    );
  }

  const { project, units } = q.data;
  const price = computeUnitPrice(unit);
  // Projekt-szintű ár-elrejtés: törölt referencia árak VAGY kézi kapcsoló.
  // Csak a MEGJELENÍTÉST érinti — a számítás és az admin nézet változatlan.
  const pricesHidden = arePricesHidden(project.pricingDefaults);
  const sortedUnits = [...units].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label),
  );
  const currentIdx = sortedUnits.findIndex((u) => u.id === unit.id);
  const prevUnit = currentIdx > 0 ? sortedUnits[currentIdx - 1] : null;
  const nextUnit =
    currentIdx >= 0 && currentIdx < sortedUnits.length - 1
      ? sortedUnits[currentIdx + 1]
      : null;

  return (
    <div className="bg-white">
      {/* Header band */}
      <section className="border-b border-brand-100 bg-brand-50/50">
        <div className="container py-8">
          <Link
            href={`/projektek/${project.slug}`}
            onClick={() => navigate(`/projektek/${project.slug}`)}
            className="relative z-40 inline-flex items-center gap-1.5 text-xs uppercase tracking-widish font-semibold text-brand-500 hover:text-accent transition-colors"
          >
            <ArrowLeft size={14} />
            <span>{project.name}</span>
          </Link>

          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="section-eyebrow">Lakás</span>
              <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-heading font-normal uppercase tracking-ultrawide text-brand-700">
                {unit.label}{" "}
                <span className="text-accent">lakás</span>
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <span
                  className={`px-3 py-1 text-xs uppercase tracking-widish font-semibold ${UNIT_STATUS_BADGE[unit.status]}`}
                >
                  {UNIT_STATUS_LABEL[unit.status]}
                </span>
                {unit.rooms && (
                  <span className="text-brand-500">{unit.rooms} szoba</span>
                )}
                {unit.areaSqm && (
                  <span className="text-brand-500">
                    · {formatArea(unit.areaSqm as unknown as number)}
                  </span>
                )}
                {unit.floor != null && (
                  <span className="text-brand-500">· {unit.floor}. emelet</span>
                )}
              </div>
            </div>
            {pricesHidden ? (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widish text-brand-400 font-semibold">
                  Vételár
                </div>
                {/* Ár helyett ajánlatkérés — egy oldalon belüli horgony, ami
                    az „Érdekel ez a lakás?" űrlaphoz görget (a lakás adataival). */}
                <a
                  href={`#${QUOTE_ANCHOR}`}
                  className="font-heading text-2xl lg:text-3xl text-accent hover:underline"
                >
                  {REQUEST_QUOTE_LABEL}
                </a>
              </div>
            ) : (
              (price.hasItems || price.hasPrice) && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widish text-brand-400 font-semibold">
                  {price.hasItems ? "Bruttó vételár" : "Vételár"}
                </div>
                <div className="font-heading text-2xl lg:text-3xl text-brand-700">
                  {formatPriceHuf(price.grossHuf)}
                </div>
                {/* A választható extrák csak az „Ár összetétele" táblázatban
                    jelennek meg — a fejlécben elég a bruttó vételár
                    (F-MANUAL-IBV541UF). */}
              </div>
              )
            )}
          </div>
        </div>
      </section>

      <div className="container py-12 lg:py-16 grid lg:grid-cols-3 gap-12">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-14">
          {/* Gallery */}
          {unit.gallery.length > 0 && (
            <ScrollReveal>
              <ImageGallery items={unit.gallery} />
            </ScrollReveal>
          )}

          {/* Description */}
          {unit.description && (
            <ScrollReveal>
              <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-4">
                Leírás
              </h2>
              <p className="text-brand-600 leading-relaxed whitespace-pre-line">
                {unit.description}
              </p>
            </ScrollReveal>
          )}

          {/* Floorplans */}
          {unit.floorplans.length > 0 && (
            <ScrollReveal>
              <UnitFloorplans floorplans={unit.floorplans} />
            </ScrollReveal>
          )}

          {/* Style selector */}
          {unit.stylePreviews.length > 0 && (
            <ScrollReveal>
              <StyleSelector previews={unit.stylePreviews} />
            </ScrollReveal>
          )}

          {/* Virtual tour */}
          {unit.virtualTourUrl && (
            <ScrollReveal>
              <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-1 flex items-center gap-2">
                <PlayCircle size={20} className="text-accent" />
                Virtuális séta
              </h2>
              <p className="text-sm text-brand-500 mb-4">
                Sétáljon végig az ingatlanon — egér / érintés / VR
                szemüveggel. A példa séta admin felületen lecserélhető a
                projekt saját Matterport / Pannellum / Kuula URL-jére.
              </p>
              <div className="relative aspect-[16/9] bg-brand-900">
                <iframe
                  src={unit.virtualTourUrl}
                  className="absolute inset-0 w-full h-full border-0"
                  title="Virtuális séta"
                  allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </ScrollReveal>
          )}

          {/* Neighborhood */}
          {(project.locationAddress ||
            (project.locationLat && project.locationLng) ||
            project.neighborhoodPois.length > 0 ||
            project.neighborhoodDescription) && (
            <ScrollReveal>
              <Neighborhood
                address={project.locationAddress}
                lat={
                  project.locationLat != null
                    ? Number(project.locationLat)
                    : null
                }
                lng={
                  project.locationLng != null
                    ? Number(project.locationLng)
                    : null
                }
                pois={project.neighborhoodPois}
                description={project.neighborhoodDescription}
              />
            </ScrollReveal>
          )}

          {/* Ár összetétele (tételes bontás) — kézi ár-felülírásnál elrejtve,
              mert a kézi (végleges) ár felülírja a tételes levezetést, így a
              bontás félrevezető lenne (F-MANUAL-O25N9HIR). */}
          {price.hasItems && (!price.hasOverride || pricesHidden) && (
            <ScrollReveal>
              <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-4 flex items-center gap-2">
                <Receipt size={20} className="text-accent" />
                Ár összetétele
              </h2>
              <PriceBreakdown
                price={price}
                pricesHidden={pricesHidden}
                quoteAnchor={`#${QUOTE_ANCHOR}`}
              />
              <p className="mt-3 text-xs text-brand-400">
                {pricesHidden
                  ? "Az opcionális tételek (garázs, parkolóhely, tároló) külön vásárolhatók meg. A vételárról és az extrákról egyedi ajánlatot adunk — kérjen ajánlatot a lenti űrlapon."
                  : "A feltüntetett árak tájékoztató jellegűek. Az opcionális tételek (garázs, parkolóhely, tároló) külön vásárolhatók meg."}
              </p>
            </ScrollReveal>
          )}

          {/* Hitel kalkulátor — jelenleg elrejtve (MORTGAGE_CALCULATOR_ENABLED),
              a kód és az admin beállítások a háttérben megmaradnak. */}
          {MORTGAGE_CALCULATOR_ENABLED &&
            !pricesHidden &&
            (price.hasItems || price.hasPrice) &&
            siteSettings.mortgage?.enabled !== false && (
              <ScrollReveal>
                <MortgageCalculator
                  initialPrice={price.grossHuf}
                  settings={siteSettings.mortgage}
                />
              </ScrollReveal>
            )}

          {/* Hitelügyintézés — a megnyitott űrlapban ez a lakás az
              alapértelmezett választás. */}
          <ScrollReveal>
            <MortgageAdvisorBox unitId={unit.id} variant="card" />
          </ScrollReveal>
        </div>

        {/* Sidebar — tech specs + interest form */}
        <aside className="lg:sticky lg:top-24 lg:self-start space-y-8">
          <ScrollReveal>
            <div className="card p-6 space-y-4">
              <h3 className="font-semibold uppercase tracking-widish text-xs text-brand-500">
                Műszaki adatok
              </h3>
              <SpecRow icon={Ruler} label="Alapterület" value={formatArea(unit.areaSqm as unknown as number | null)} />
              {unit.rooms && (
                <SpecRow icon={Home} label="Szobaszám" value={unit.rooms} />
              )}
              {unit.floor != null && (
                <SpecRow icon={Layers} label="Emelet" value={`${unit.floor}.`} />
              )}
              {unit.orientation && (
                <SpecRow icon={Compass} label="Tájolás" value={unit.orientation} />
              )}
              {unit.balconyAreaSqm && (
                <SpecRow icon={Sun} label="Erkély" value={formatArea(unit.balconyAreaSqm as unknown as number)} />
              )}
              {unit.terraceAreaSqm && (
                <SpecRow icon={Sun} label="Terasz" value={formatArea(unit.terraceAreaSqm as unknown as number)} />
              )}
              {unit.gardenAreaSqm && (
                <SpecRow icon={Trees} label="Saját kert" value={formatArea(unit.gardenAreaSqm as unknown as number)} />
              )}
              {unit.energyRating && (
                <SpecRow icon={Zap} label="Energiabesorolás" value={unit.energyRating} />
              )}
              {unit.heatingType && (
                <SpecRow icon={Thermometer} label="Fűtés" value={unit.heatingType} />
              )}
              {unit.parkingIncluded && (
                <SpecRow
                  icon={ParkingCircle}
                  label="Parkoló"
                  value={unit.parkingCount ? `${unit.parkingCount} db` : "Igen"}
                />
              )}
              {unit.storageIncluded && (
                <SpecRow
                  icon={Package}
                  label="Tároló"
                  value={
                    unit.storageAreaSqm
                      ? formatArea(unit.storageAreaSqm as unknown as number)
                      : "Igen"
                  }
                />
              )}
            </div>

            <div id={QUOTE_ANCHOR} className="card p-6 mt-4 scroll-mt-24">
              <h3 className="font-semibold uppercase tracking-widish text-xs text-brand-500 mb-4">
                {pricesHidden ? "Kérjen ajánlatot erre a lakásra" : "Érdekel ez a lakás?"}
              </h3>
              <DynamicForm
                projectId={project.id}
                unitId={unit.id}
                unitLabel={`${project.name} — ${unit.label}`}
                compact
              />
            </div>
          </ScrollReveal>
        </aside>
      </div>

      {/* Prev / next + tech specs link */}
      <section className="border-t border-brand-100 bg-brand-50">
        <div className="container py-8 grid sm:grid-cols-3 gap-6 items-center">
          <div>
            {prevUnit && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/projektek/${project.slug}/${unitSlug(prevUnit)}`)
                }
                className="group inline-flex items-center gap-2 text-sm uppercase tracking-widish font-semibold text-brand-500 hover:text-accent transition-colors"
              >
                <ArrowLeft size={14} />
                <span>Előző · {prevUnit.label}</span>
              </button>
            )}
          </div>
          <div className="text-center">
            <Link
              href={`/projektek/${project.slug}/muszaki-adatok`}
              className="btn-secondary inline-flex"
            >
              <KeyRound size={14} />
              Részletes műszaki adatok
            </Link>
          </div>
          <div className="text-right">
            {nextUnit && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/projektek/${project.slug}/${unitSlug(nextUnit)}`)
                }
                className="group inline-flex items-center gap-2 text-sm uppercase tracking-widish font-semibold text-brand-500 hover:text-accent transition-colors"
              >
                <span>Következő · {nextUnit.label}</span>
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Unit-page floorplans. Image floorplans flagged `displayMode === 'inline'` are
 * rendered directly as images (click opens an in-page lightbox); everything else
 * (PDFs, or floorplans without an explicit inline flag) keeps the card → new-tab
 * behaviour. Backward-compatible: missing `displayMode` ⇒ card mode.
 */
function UnitFloorplans({ floorplans }: { floorplans: Floorplan[] }) {
  const isInline = (fp: Floorplan) =>
    fp.displayMode === "inline" && fp.mimeType.startsWith("image/");
  const inlineItems = floorplans.filter(isInline);
  const cardItems = floorplans.filter((fp) => !isInline(fp));

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lightboxItems = useMemo(
    () =>
      inlineItems.map((fp) => ({
        key: fp.key,
        alt: fp.label,
        mimeType: fp.mimeType,
      })),
    [inlineItems],
  );

  return (
    <>
      <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-4">
        Alaprajzok
      </h2>

      {inlineItems.length > 0 && (
        <div className="space-y-6 mb-6">
          {inlineItems.map((fp, i) => (
            <figure key={fp.key}>
              <button
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="group relative block w-full overflow-hidden bg-brand-100 cursor-zoom-in"
                aria-label={`${fp.label} – nagyítás`}
              >
                <img
                  src={fileUrl(fp.key)}
                  alt={fp.label}
                  loading="lazy"
                  className="w-full max-h-[70vh] object-contain"
                />
                <span className="absolute top-3 right-3 w-9 h-9 grid place-items-center bg-white/90 group-hover:bg-white text-brand-700 transition-colors">
                  <Maximize2 size={15} />
                </span>
              </button>
              <figcaption className="mt-2 text-sm font-medium text-brand-600">
                {fp.label}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {cardItems.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {cardItems.map((fp) => (
            <a
              key={fp.key}
              href={fileUrl(fp.key)}
              target="_blank"
              rel="noopener noreferrer"
              className="card p-4 flex items-center gap-3 hover:bg-brand-50 transition-colors group"
            >
              <FileText size={24} className="text-accent shrink-0" />
              <div className="flex-1 text-sm">
                <div className="font-medium text-brand-700 group-hover:text-accent transition-colors">
                  {fp.label}
                </div>
                <div className="text-xs text-brand-400">{fp.mimeType}</div>
              </div>
              <Maximize2 size={14} className="text-brand-400" />
            </a>
          ))}
        </div>
      )}

      {lightboxIndex !== null && lightboxItems.length > 0 && (
        <Lightbox
          items={lightboxItems}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNext={() =>
            setLightboxIndex((i) => ((i ?? 0) + 1) % lightboxItems.length)
          }
          onPrev={() =>
            setLightboxIndex(
              (i) => ((i ?? 0) - 1 + lightboxItems.length) % lightboxItems.length,
            )
          }
        />
      )}
    </>
  );
}

/**
 * Tételes ár-bontás táblázat (nettó + ÁFA + bruttó), opcionális extrákkal.
 * Dinamikusan méreteződik (w-full + table-fixed) vízszintes görgetősáv
 * nélkül: md alatt a különálló Nettó és ÁFA oszlop elrejtve, az adat a
 * Bruttó érték alatt jelenik meg kompakt formában (F-MANUAL-4ROWTYY8).
 */
function PriceBreakdown({
  price,
  pricesHidden = false,
  quoteAnchor,
}: {
  price: UnitPrice;
  /** Ar-elrejtes: a penz-oszlopok eltunnek, a tetelek (megnevezes + mennyiseg)
   *  maradnak, es KIZAROLAG a vetelar osszegenek helyen all a felhivas. */
  pricesHidden?: boolean;
  quoteAnchor?: string;
}) {
  const required = price.lines.filter((l) => !l.optional);
  const optional = price.lines.filter((l) => l.optional);
  // Kiválasztható opcionális tételek (F-MANUAL-IBV541UF): a pipált extrák a
  // lakás árával együtt tételes összesítőt adnak a táblázat alján.
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const togglePicked = (i: number) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const pickedLines = optional.filter((_, i) => picked.has(i));
  const sumNet = price.netHuf + pickedLines.reduce((s, l) => s + l.netHuf, 0);
  const sumGross =
    price.grossHuf + pickedLines.reduce((s, l) => s + l.grossHuf, 0);
  return (
    <div className="rounded-xl border border-brand-100 overflow-hidden">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-brand-50 text-[10px] uppercase tracking-widish text-brand-500">
          <tr>
            <th className="w-[30%] md:w-[26%] text-left px-2 sm:px-3 xl:px-4 py-3 font-semibold">
              Tétel
            </th>
            <th
              className={`text-right px-2 sm:px-3 xl:px-4 py-3 font-semibold ${
                pricesHidden ? "w-[70%] md:w-[74%]" : "w-[32%] md:w-[20%]"
              }`}
            >
              Mennyiség
            </th>
            {!pricesHidden && (
              <>
                <th className="hidden md:table-cell md:w-[18%] text-right px-2 sm:px-3 xl:px-4 py-3 font-semibold">
                  Nettó
                </th>
                <th className="hidden md:table-cell md:w-[17%] text-right px-2 sm:px-3 xl:px-4 py-3 font-semibold">
                  ÁFA
                </th>
                <th className="w-[38%] md:w-[19%] text-right px-2 sm:px-3 xl:px-4 py-3 font-semibold">
                  Bruttó
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {required.map((l, i) => (
            <PriceLineRow key={i} line={l} pricesHidden={pricesHidden} />
          ))}
          {pricesHidden ? (
            // Ar-elrejtes: az EGYETLEN hely, ahol a felhivas szerepel - a
            // vetelar osszegenek helyen (a tetel-sorok neman maradnak).
            <tr className="border-t-2 border-brand-200 font-semibold bg-brand-50/60">
              <td className="px-2 sm:px-3 xl:px-4 py-3">Vételár</td>
              <td className="px-2 sm:px-3 xl:px-4 py-3 text-right">
                {quoteAnchor ? (
                  <a href={quoteAnchor} className="text-accent hover:underline">
                    {REQUEST_QUOTE_LABEL}
                  </a>
                ) : (
                  <span className="text-accent">{REQUEST_QUOTE_LABEL}</span>
                )}
              </td>
            </tr>
          ) : (
          <tr className="border-t-2 border-brand-200 font-semibold bg-brand-50/60">
            <td className="px-2 sm:px-3 xl:px-4 py-3">Bruttó vételár</td>
            <td className="px-2 sm:px-3 xl:px-4 py-3" />
            <td className="hidden md:table-cell px-2 sm:px-3 xl:px-4 py-3 text-right tabular-nums text-brand-600">
              {formatPriceHuf(price.netHuf)}
            </td>
            <td className="hidden md:table-cell px-2 sm:px-3 xl:px-4 py-3 text-right tabular-nums text-brand-600">
              {formatPriceHuf(price.vatHuf)}
            </td>
            <td className="px-2 sm:px-3 xl:px-4 py-3 text-right tabular-nums text-brand-800">
              {formatPriceHuf(price.grossHuf)}
              <span className="block md:hidden text-[10px] font-normal text-brand-400">
                nettó {formatPriceHuf(price.netHuf)} + ÁFA{" "}
                {formatPriceHuf(price.vatHuf)}
              </span>
            </td>
          </tr>
          )}
          {optional.length > 0 && (
            <>
              <tr>
                <td
                  colSpan={pricesHidden ? 2 : 5}
                  className="px-2 sm:px-3 xl:px-4 pt-5 pb-1 text-[10px] uppercase tracking-widish text-brand-400 font-semibold"
                >
                  {pricesHidden
                    ? "Opcionálisan megvásárolható"
                    : "Opcionálisan megvásárolható — jelölje be, amit a lakáshoz választana"}
                </td>
              </tr>
              {optional.map((l, i) =>
                pricesHidden ? (
                  // Ar nelkul a kivalasztasos osszesitonek nincs ertelme - a
                  // tetelek csak felsorolaskent (megnevezes + mennyiseg) allnak.
                  <PriceLineRow key={i} line={l} optional pricesHidden />
                ) : (
                  <PriceLineRow
                    key={i}
                    line={l}
                    optional
                    picked={picked.has(i)}
                    onTogglePick={() => togglePicked(i)}
                  />
                ),
              )}
            </>
          )}
          {!pricesHidden && pickedLines.length > 0 && (
            <>
              <tr>
                <td
                  colSpan={5}
                  className="px-2 sm:px-3 xl:px-4 pt-5 pb-1 text-[10px] uppercase tracking-widish text-accent font-semibold"
                >
                  Összesítő a kiválasztott tételekkel
                </td>
              </tr>
              <tr className="border-t border-brand-100 text-brand-600">
                <td className="px-2 sm:px-3 xl:px-4 py-2" colSpan={4}>
                  Lakás bruttó vételára
                </td>
                <td className="px-2 sm:px-3 xl:px-4 py-2 text-right tabular-nums">
                  {formatPriceHuf(price.grossHuf)}
                </td>
              </tr>
              {pickedLines.map((l, i) => (
                <tr key={i} className="border-t border-brand-100 text-brand-600">
                  <td className="px-2 sm:px-3 xl:px-4 py-2" colSpan={4}>
                    {l.label}
                    <span className="text-brand-400 text-xs">
                      {" "}
                      (nettó {formatPriceHuf(l.netHuf)} + {l.vatPct}% ÁFA)
                    </span>
                  </td>
                  <td className="px-2 sm:px-3 xl:px-4 py-2 text-right tabular-nums">
                    {formatPriceHuf(l.grossHuf)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-accent/40 font-semibold bg-accent/5">
                <td className="px-2 sm:px-3 xl:px-4 py-3" colSpan={4}>
                  Végösszeg a kiválasztott tételekkel
                  <span className="block text-[10px] font-normal text-brand-500">
                    nettó {formatPriceHuf(sumNet)} + ÁFA{" "}
                    {formatPriceHuf(sumGross - sumNet)}
                  </span>
                </td>
                <td className="px-2 sm:px-3 xl:px-4 py-3 text-right tabular-nums text-brand-800">
                  {formatPriceHuf(sumGross)}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PriceLineRow({
  line,
  optional,
  picked,
  onTogglePick,
  pricesHidden = false,
}: {
  line: PriceLine;
  optional?: boolean;
  picked?: boolean;
  onTogglePick?: () => void;
  pricesHidden?: boolean;
}) {
  return (
    <tr className="border-t border-brand-100">
      <td className="px-2 sm:px-3 xl:px-4 py-3">
        {optional && onTogglePick && (
          <input
            type="checkbox"
            className="mr-2 align-middle accent-[var(--accent,#2f7a3e)]"
            checked={!!picked}
            onChange={onTogglePick}
            aria-label={`${line.label} kiválasztása a lakáshoz`}
          />
        )}
        <span className={optional ? "text-brand-600" : "text-brand-800"}>
          {line.label}
        </span>
        {optional && (
          <span className="ml-2 rounded bg-accent/10 text-accent px-1.5 py-0.5 text-[9px] uppercase tracking-widish font-bold align-middle">
            opcionális
          </span>
        )}
      </td>
      <td className="px-2 sm:px-3 xl:px-4 py-3 text-right text-brand-500 tabular-nums">
        {line.quantity.toLocaleString("hu-HU")}{" "}
        {line.unit === "sqm" ? "m²" : "db"}
        {/* Ar-elrejtesnel a m2-ar / darabar sem jelenhet meg. */}
        {!pricesHidden && (
          <span className="text-brand-400">
            {" "}
            × {formatPriceHuf(line.unitPriceHuf)}
          </span>
        )}
      </td>
      {!pricesHidden && (
        <>
          <td className="hidden md:table-cell px-2 sm:px-3 xl:px-4 py-3 text-right tabular-nums text-brand-600">
            {formatPriceHuf(line.netHuf)}
          </td>
          <td className="hidden md:table-cell px-2 sm:px-3 xl:px-4 py-3 text-right tabular-nums text-brand-500">
            {formatPriceHuf(line.vatHuf)}
            <span className="text-[10px] text-brand-400"> ({line.vatPct}%)</span>
          </td>
          <td className="px-2 sm:px-3 xl:px-4 py-3 text-right tabular-nums font-medium text-brand-800">
            {formatPriceHuf(line.grossHuf)}
            <span className="block md:hidden text-[10px] font-normal text-brand-400">
              nettó {formatPriceHuf(line.netHuf)} + {line.vatPct}% ÁFA
            </span>
          </td>
        </>
      )}
    </tr>
  );
}

function SpecRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm py-2 border-b border-brand-100 last:border-0">
      <div className="flex items-center gap-2.5 text-brand-500">
        <Icon size={15} className="text-brand-400" strokeWidth={1.6} />
        <span className="text-xs uppercase tracking-widish font-medium">
          {label}
        </span>
      </div>
      <span className="text-brand-700 font-medium text-right">{value}</span>
    </div>
  );
}

function StyleSelector({ previews }: { previews: StylePreview[] }) {
  const [active, setActive] = useState(0);
  const [vrOpen, setVrOpen] = useState(false);
  const current = previews[active];

  // Gallery follows the active style. Only the selected style's own images are
  // shown — the project's main gallery must never leak onto the unit page
  // (F-MANUAL-EZWGLODK); the unit's own gallery is already rendered above.
  const styleGallery = current.galleryImages ?? [];

  function selectStyle(i: number) {
    setActive(i);
    setVrOpen(false);
  }

  return (
    <div>
      <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-1">
        Belső stílus választó
      </h2>
      <p className="text-sm text-brand-500 mb-5">
        Tekintse meg, hogyan néz ki a lakás különböző belsőépítészeti
        stílusban — a kivitelezés a vásárló választása alapján történik.
      </p>

      <div className="relative aspect-[16/10] overflow-hidden bg-brand-200 mb-4">
        <img
          key={current.imageKey}
          src={fileUrl(current.imageKey)}
          alt={current.label}
          className="w-full h-full object-cover transition-opacity duration-300"
        />
        {current.vrTourUrl && (
          <button
            type="button"
            onClick={() => setVrOpen(true)}
            className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-white/90 hover:bg-white text-brand-900 text-xs uppercase tracking-widish font-semibold px-4 py-2.5 shadow-sm transition-colors"
          >
            <View size={16} className="text-accent" />
            VR séta
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {previews.map((p, i) => (
          <button
            type="button"
            key={p.label + i}
            onClick={() => selectStyle(i)}
            className={`text-left border-2 p-3 transition-colors ${
              active === i
                ? "border-accent bg-accent/5"
                : "border-brand-200 hover:border-brand-400"
            }`}
          >
            <div className="relative aspect-[4/3] bg-brand-100 mb-2 overflow-hidden">
              <img
                src={fileUrl(p.imageKey)}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
              {p.vrTourUrl && (
                <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 bg-brand-900/80 text-white text-[9px] uppercase tracking-widish font-semibold px-1.5 py-0.5">
                  <View size={10} /> VR
                </span>
              )}
            </div>
            <div
              className={`text-xs uppercase tracking-widish font-semibold ${active === i ? "text-accent" : "text-brand-700"}`}
            >
              {p.label}
            </div>
            {p.description && (
              <div className="text-[11px] text-brand-500 mt-1 line-clamp-2">
                {p.description}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Style-specific gallery — only the selected style's own images. */}
      {styleGallery.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold uppercase tracking-widish text-brand-700 mb-3">
            {current.label} galéria
          </h3>
          <ImageGallery key={current.imageKey} items={styleGallery} />
        </div>
      )}

      {vrOpen && current.vrTourUrl && (
        <VrTourModal url={current.vrTourUrl} onClose={() => setVrOpen(false)} />
      )}
    </div>
  );
}

function VrTourModal({
  url,
  onClose,
}: {
  url: string;
  onClose: () => void;
}) {
  // Escape to close + lock body scroll while open (matches the lightbox).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Virtuális séta"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-6 right-6 w-12 h-12 grid place-items-center text-white/80 hover:text-accent transition-colors"
        aria-label="Bezár"
      >
        <X size={24} />
      </button>
      <div
        className="relative w-full max-w-6xl aspect-[16/9] bg-brand-900"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={url}
          className="absolute inset-0 w-full h-full border-0"
          title="Virtuális séta"
          allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer"
          allowFullScreen
        />
      </div>
    </div>
  );
}
